import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { authApi } from '../api/auth.api.js';

import { useAuth } from './useAuth.js';

/** Uses the existing sign-in card; provisioning secrets never leave this browser/API. */
export function MfaEnrollmentForm({ onComplete }: { onComplete: () => void }): JSX.Element {
  const { user, logout } = useAuth();
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const setup = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try { setSecret((await authApi.setupTotp()).secret); }
    catch (err) { setError(err instanceof Error ? err.message : 'Authenticator setup could not start. Try again.'); }
    finally { setBusy(false); }
  };
  const verify = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await authApi.verifyTotpSetup(code);
      setSecret('');
      onComplete();
      await logout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code could not be verified. Try again.');
    } finally { setBusy(false); }
  };

  return <Stack spacing={2}>
    <Typography variant="h4">Set up your authenticator</Typography>
    <Typography color="text.secondary">Protect access for {user?.email}. Add this account to your authenticator app before entering the back office.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {!secret ? <Button variant="contained" disabled={busy} onClick={() => void setup()}>
      {busy ? 'Starting…' : 'Start authenticator setup'}
    </Button> : <Stack component="form" onSubmit={(event) => void verify(event)} spacing={2}>
      <Typography variant="body2">In your authenticator, choose “Enter a setup key”. Name the account Norvix, enter the key below and select “Time based”.</Typography>
      <TextField label="Setup key" value={secret} InputProps={{ readOnly: true }}
        inputProps={{ 'aria-label': 'Setup key' }} helperText="Keep this key private. It is only shown during setup." fullWidth />
      <TextField label="Authenticator code" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
        inputProps={{ pattern: '[0-9]{6}', maxLength: 6, inputMode: 'numeric', autoComplete: 'one-time-code' }} required fullWidth />
      <Button type="submit" variant="contained" disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Verify and finish setup'}</Button>
    </Stack>}
    <Button disabled={busy} onClick={() => void logout()}>Cancel and sign out</Button>
  </Stack>;
}
